import * as React from 'react';
import {
  useItemExistsInWishList,
  removeWishListItem,
  addWishListItem,
  useWishListCount,
} from '@avensia/excite-commercetools';
import { styled } from '@glitz/react';
import { Theme } from 'webapp/theme';
import { translate, useMatchMobile } from '@avensia/excite';
import { type PropType as ButtonPropType, FeedbackStandaloneButton } from 'webapp/components/button';
import { createMaxMobileViewportDecorator, createMinMediumViewportDecorator } from 'webapp/viewport';
import { FieldSize } from 'webapp/components/field-size';
import { HeartIcon } from 'webapp/components/icons';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { ariaLiveObservable } from 'webapp/shared/aria-live-region';

type PropType = ButtonPropType & { product: NordicNest.Api.PartialProductApiModel; location: string; name: string };
export const WishlistButton = styled(({ product, location, name, ...restProps }: PropType) => {
  const isMobile = useMatchMobile();
  const request: Excite.Commercetools.WishlistRequest = {
    sku: product.quickBuyPackage ? '' : product.sku,
  };
  const existInWishList = useItemExistsInWishList(request);
  const count = useWishListCount();

  const toggleWishlistItem = async () => {
    try {
      if (existInWishList) {
        await removeWishListItem(request, { ecommerce: { button_location: location, button_name: name } });
        ariaLiveObservable.emit({
          message: translate(t => t.wishlist.totalItems, { count: count - 1 }),
        });
      } else {
        const response = await addWishListItem(request, {
          ecommerce: { button_location: location, button_name: name },
        });

        if (response.success) {
          ariaLiveObservable.emit({
            message: translate(t => t.wishlist.totalItems, { count: count + 1 }),
          });
        } else {
          throw new Error('Failed to add to wishlist');
        }
      }
    } catch (error) {
      console.error('Wishlist operation failed:', error);
      ariaLiveObservable.emit({
        message: translate(t => t.wishlist.addToWishlistError, { count: count }),
      });
    }
  };

  return product.isPackage || product.isBundle ? null : (
    <Button
      disableRippleEffect
      {...restProps}
      onClick={toggleWishlistItem}
      as={Theme.WishlistButton}
      size={FieldSize.XSmall}
      aria-label={translate(t => t.wishlist.addToWishlistAriaLabel, { product: product.displayName })}
      aria-pressed={existInWishList}
      {...testIdProperty(existInWishList)}
    >
      {!isMobile && (
        <>
          {existInWishList ? translate(t => t.wishlist.removeFromWishlist) : translate(t => t.wishlist.addToWishlist)}
        </>
      )}

      <FavoriteIcon aria-hidden="true" css={existInWishList && likedDecorator} />
    </Button>
  );
});

function testIdProperty(existInWishList: boolean) {
  if (existInWishList) {
    return NnTestIdProps.MyWishListElementRemove;
  }
  return NnTestIdProps.AddToWishListButton;
}

const Button = styled(
  FeedbackStandaloneButton,
  createMaxMobileViewportDecorator({
    fontSize: t => t.sharedFontSize.xxs,
  }),
  {
    height: 41,
    fontWeight: 400,
    position: 'relative',
    width: '100%',
    fontSize: t => t.sharedFontSize.xs,
  },
);

const likedDecorator = styled({
  color: t => t.productCard?.wishlistIcon?.color,
  fill: t => t.productCard?.wishlistIcon?.color,
});

const FavoriteIcon = styled(
  HeartIcon,
  createMinMediumViewportDecorator({
    position: 'absolute',
    right: 12,
  }),
  {
    width: 19,
    height: 19,
    fill: 'none',
  },
);
